/** Enveloppe applicative : en-tête, navigation, zone de contenu. */

import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

import { notificationsApi } from "../api/endpoints";
import { useSession } from "../hooks/SessionContext";
import { Button } from "./ui";

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

const MAIN_NAV: NavItem[] = [
  { to: "/", label: "Tableau de bord", end: true },
  { to: "/demarches", label: "Toutes les démarches" },
  { to: "/documents", label: "Justificatifs" },
  { to: "/historique", label: "Historique" },
  { to: "/notifications", label: "Notifications" },
  { to: "/bibliotheque", label: "Bibliothèque administrative" },
];

const SETTINGS_NAV: NavItem[] = [
  { to: "/profil-entreprise", label: "Profil entreprise" },
  { to: "/parametres", label: "Paramètres" },
  { to: "/securite", label: "Sécurité" },
  { to: "/donnees", label: "Données & sauvegardes" },
  { to: "/corbeille", label: "Corbeille et archives" },
];

export function AppLayout({ children }: { children: ReactNode }) {
  const { user, currentBusiness, signOut } = useSession();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  // Compteur de notifications non lues, rafraîchi périodiquement.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await notificationsApi.unreadCount();
        const count = Number(response.data?.count ?? 0);
        if (!cancelled) setUnread(count);
      } catch {
        /* silencieux : un compteur indisponible ne doit pas gêner */
      }
    };
    void load();
    const timer = window.setInterval(load, 120_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#contenu">
        Aller au contenu principal
      </a>
      <header className="app-header">
        <Link to="/" className="app-header__brand">
          <span className="app-header__mark" aria-hidden="true">
            AV
          </span>
          <span>ArkVibes Gestion</span>
        </Link>
        <span className="app-header__spacer" />
        <div className="app-header__actions">
          {currentBusiness && (
            <span className="badge" title="Entreprise courante">
              {currentBusiness.trade_name}
            </span>
          )}
          <Button
            variant="quiet"
            size="sm"
            onClick={async () => {
              await signOut();
              navigate("/connexion", { replace: true });
            }}
          >
            Se déconnecter
          </Button>
        </div>
      </header>

      <div className="app-body">
        <nav className="app-nav" aria-label="Navigation principale">
          <ul className="app-nav__list">
            {MAIN_NAV.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} end={item.end} className="app-nav__link">
                  <span>{item.label}</span>
                  {item.to === "/notifications" && unread > 0 && (
                    <span className="app-nav__badge">
                      {unread}
                      <span className="visually-hidden"> non lues</span>
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
            <li aria-hidden="true">
              <hr style={{ margin: "0.5rem 0" }} />
            </li>
            {SETTINGS_NAV.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} className="app-nav__link">
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <main className="app-main" id="contenu">
          {children}
        </main>
      </div>

      <footer className="app-footer">
        <p className="mb-0">
          ArkVibes Gestion — {user?.email}. Les obligations affichées proviennent
          de sources officielles citées ; celles marquées « à vérifier » doivent
          être confirmées avant toute décision.
        </p>
      </footer>
    </div>
  );
}

export function AuthLayout({
  title,
  intro,
  children,
  footer,
}: {
  title: string;
  intro?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="auth-layout">
      <div className="auth-card">
        <div className="auth-card__brand">
          <span className="app-header__mark" aria-hidden="true">
            AV
          </span>
          <strong>ArkVibes Gestion</strong>
        </div>
        <h1 className="auth-card__title">{title}</h1>
        {intro && <p className="auth-card__intro">{intro}</p>}
        {children}
        {footer && <div className="auth-card__footer">{footer}</div>}
      </div>
    </div>
  );
}
